import React, { useState, useEffect, memo } from 'react';
import { useLocation, Navigate } from 'react-router-dom';
import Header from './Header';
import Sidebar from './Sidebar';
import BottomNav from './BottomNav';
import InstallPrompt from '../InstallPrompt';
import { X } from 'lucide-react'; // Usamos el icono consistente
import ActiveCampaignBanner from '../../features/campaigns/components/ActiveCampaignBanner';
import PlanReadOnlyBanner from '../PlanReadOnlyBanner';
import ContactLimitBanner from '../ContactLimitBanner';
import WaPauseBanner from '../WaPauseBanner';
import ManagerTour from '../ManagerTour';
import { useAuth } from '../../context/AuthContext';
import { canOpenPath } from '../../utils/modulePermissions';

// Memoize the Header component to prevent unnecessary re-renders
const MemoizedHeader = memo(Header);

interface LayoutProps {
  children: React.ReactNode;
  /** Oculta la barra lateral fija de escritorio (pantalla amplia; p. ej. el tablero de Repartos). El drawer móvil sigue igual. */
  hideSidebar?: boolean;
}

export const Layout: React.FC<LayoutProps> = ({ children, hideSidebar = false }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { currentUser } = useAuth();
  const { pathname } = useLocation();

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  // Módulo cross "tour": los pasos que apuntan a ítems del menú abren/cierran el
  // drawer móvil por evento (en desktop el sidebar es fijo y esto es no-op visual).
  useEffect(() => {
    const openIt = () => setSidebarOpen(true);
    const closeIt = () => setSidebarOpen(false);
    window.addEventListener('lealify:open-sidebar', openIt);
    window.addEventListener('lealify:close-sidebar', closeIt);
    return () => {
      window.removeEventListener('lealify:open-sidebar', openIt);
      window.removeEventListener('lealify:close-sidebar', closeIt);
    };
  }, []);

  // Guard suave por módulo (F3): si el operador tiene la pantalla en "Sin acceso",
  // redirige a Inicio (el backend igual rechaza con 403; esto es UX). El mapa
  // pantalla→módulo es el mismo que oculta los ítems del nav.
  if (currentUser && !canOpenPath(currentUser, pathname)) {
    return <Navigate to="/dashboard" replace />;
  }

  // Rediseño «Marca» (opción B, 8-oct-2026): barra navy a toda la altura con el logo, y a la derecha
  // encabezado + contenido a TODO lo ancho (antes: encabezado arriba de todo y contenido topado a 1280 px).
  return (
    <div className="h-screen flex overflow-hidden bg-canvas">
      {/* Barra lateral fija (tablet y escritorio) */}
      <div className={hideSidebar ? 'hidden' : 'hidden md:flex h-screen flex-shrink-0'}>
        <Sidebar />
      </div>

      <div className="flex-1 min-w-0 flex flex-col">
        <MemoizedHeader toggleSidebar={toggleSidebar} />

        {/* Sidebar móvil (overlay). SIEMPRE montado y desplazado fuera de pantalla al
            cerrar (patrón del módulo tour: un target ausente del DOM se salta; en
            -translate-x-full sigue existiendo y el tour puede abrir el drawer). */}
        <div
          className={`md:hidden fixed inset-0 z-40 flex ${sidebarOpen ? '' : 'pointer-events-none'}`}
          aria-hidden={!sidebarOpen}
        >
          {/* Backdrop (fondo oscuro) */}
          <div
            className={`fixed inset-0 bg-black/50 transition-opacity duration-300 ${sidebarOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
            onClick={toggleSidebar}
            aria-hidden="true"
          />

          {/* Panel lateral móvil. Mide lo MISMO que el <Sidebar> (w-64): con max-w-[300px] w-full sobraban
              ~44 px del fondo claro del panel entre el menú azul y el contenido (franja blanca, Carlos 8-oct). */}
          {/* overflow-hidden (12-oct-2026, Carlos): con overflow-y-auto el panel también se arrastraba de lado
              ~48 px (la X colgaba fuera con -mr-12) y asomaba su fondo claro como franja blanca. El scroll
              vertical lo lleva el propio <Sidebar> en su lista de opciones. */}
          <div className={`relative flex flex-col w-64 max-w-[85vw] bg-surface h-full overflow-hidden shadow-xl pointer-events-auto transform transition-transform duration-300 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
            {/* Contenido del Sidebar */}
            <Sidebar />
          </div>

          {/* Spacer: aquí vive la X (antes colgaba del panel y quedaba recortada). Oculta al cerrar,
              si no queda una X flotante clickeable en el borde de la pantalla. */}
          <div className="relative flex-shrink-0 w-14" aria-hidden={!sidebarOpen}>
            <div className={`pt-2 pl-1 ${sidebarOpen ? 'pointer-events-auto' : 'hidden'}`}>
              <button
                type="button"
                className="flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
                onClick={toggleSidebar}
                aria-label="Cerrar menú"
              >
                <X className="h-6 w-6 text-white" />
              </button>
            </div>
          </div>
        </div>
        
        {/* Contenido principal de la página */}
        {/* min-w-0: sin esto, un hijo flex (flex-1) no encoge bajo su contenido (min-width:auto)
            y una tabla ancha desborda el viewport en móvil aunque tenga overflow-x-auto. */}
        <div className="flex-1 min-w-0 overflow-y-auto scrollbar-hide bg-canvas">
          {/* En móvil, espacio inferior extra para no quedar tapado por la barra/FAB */}
          <main className="pt-6 pb-[calc(5rem+env(safe-area-inset-bottom))] md:py-7">
            <div className="w-full px-4 sm:px-6 lg:px-8 2xl:px-10">
              {/* Plan vencido → modo consulta. Va primero: explica por qué no podrá guardar. */}
              <PlanReadOnlyBanner />
              {/* Plan gratis cerca o en el tope de clientes (21-sep-2026): avisa antes del 403 al registrar. */}
              <ContactLimitBanner />
              {/* Mensajes automáticos en pausa: tiene que verse desde donde se trabaja. */}
              <WaPauseBanner />
              {/* F3: progreso de campañas programadas, visible al entrar */}
              <ActiveCampaignBanner variant="global" />
              {children}
            </div>
          </main>
        </div>
      </div>

      {/* Experiencia móvil tipo app (solo móvil; escritorio intacto) */}
      <BottomNav toggleSidebar={toggleSidebar} />
      <InstallPrompt />

      {/* Tour de bienvenida al panel (coach-marks visto-una-vez, módulo cross) */}
      <ManagerTour />
    </div>
  );
};

export default Layout;